import { firestore } from "@/services/firestore";
import {
  Card,
  Container,
  CreateCardDto,
  Job,
  PatchCardDto,
  Timer,
} from "@/types/v2";
import { Card as CardV1, Job as JobV1 } from "@/types/v1";
import shortUUID from "short-uuid";
import { addCreationData, addUpdateData } from "@/lib/database";
import { customer } from "@/repositories/customer";
import { vehicle } from "@/repositories/vehicle";
import { DEFAULT_TIMER_VALUE } from "@/lib/const";
import { createConverter } from "@/repositories/common";
import { httpsCallables } from "@/repositories/httpsCallables";
import { serverNow } from "@/lib/serverClock";
import { extractCommonItems } from "../../integrations/src/lib/utils";

export const card = {
  get: async (shopId: string, cardId: string) => {
    return firestore.getDocument<Card>(
      `shops/${shopId}/cards/${cardId}`,
      createConverter<CardV1, Card>(card)
    );
  },
  // DEPRECATED. Use single subscription model
  subscribeAll: (
    id: string,
    statuses: (number | string)[],
    callback: (doc: Card[] | undefined) => void
  ) => {
    return firestore.subscribeCollectionWQueries<Card>(
      `shops/${id}/cards`,
      callback,
      [
        {
          searchParam: "statusEnum",
          opsString: "in",
          equality: [...statuses, "manuallyCreated"],
        },
        {
          searchParam: "status",
          opsString: "in",
          equality: [...statuses, "manuallyCreated"],
        },
        {
          searchParam: "repairOrderStatus" as keyof Card,
          opsString: "in",
          equality: [
            ...statuses.map((s) => `${s}`),
            ...statuses.map((s) => parseInt(`${s}`)),
            "manuallyCreated",
          ],
        },
      ],
      createConverter<CardV1, Card>(card),
      10000
    );
  },
  create: async (shopId: string, card: CreateCardDto) => {
    // Plain manual creation (no `currentContainer` in the DTO) always
    // starts new work at intake. A caller that already knows where the
    // card belongs -- e.g. Duplicate Card, which passes the source card's
    // own `currentContainer` -- lands there instead.
    const targetContainerId = card.currentContainer || "dispatch";
    const fullCard = addCreationData(
      {
        id: shortUUID.generate(),
        timers: [],
        createdVia: "dashboard",
        status: "manuallyCreated",
        currentContainer: targetContainerId,
        shopId: shopId,
        ...card,
      },
      "client"
    );
    try {
      const newCard = await firestore.createDocument(
        `shops/${shopId}/cards/${fullCard.id}`,
        fullCard
      );
      // Register the new card with its target container's own member
      // list -- `currentContainer` on the doc alone doesn't add it there;
      // moveCard does both atomically.
      await httpsCallables.moveCard({
        cardId: newCard.id,
        shopId,
        toContainerId: targetContainerId,
      });
      return newCard;
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  update: async (shopId: string, cardId: string, cardData: PatchCardDto) => {
    if (cardData._isSoftMigrated) {
      const allCardData = await card.get(shopId, cardId);
      cardData = { ...allCardData, ...cardData, shopId: shopId };
      delete cardData.createdAt;
      delete cardData.updatedAt;
      delete cardData.sourceLastUpdated;
    }
    delete cardData._isSoftMigrated;
    try {
      return await firestore.updateDocument(
        `shops/${shopId}/cards/${cardId}`,
        addUpdateData(cardData)
      );
    } catch (e) {
      console.log(e);
      throw e;
    }
  },
  markImportant: async (shopId: string, cardId: string, isStarred: boolean) => {
    return card.update(
      shopId,
      cardId,
      addUpdateData({ isStarred: isStarred } as Partial<Card>)
    );
  },
  isV1: (card: CardV1 | Card): card is CardV1 => {
    return (card as Card).schemaVersion === undefined;
  },
  transformV1ToV2: (cardData: CardV1): Card => {
    return {
      ...cardData,
      _deleted: false,
      _isSoftMigrated: true,
      isWaiter: false,
      schemaVersion: 2,
      promisedTime: cardData.repairOrder?.customerTimeOut ?? "",
      primaryField: cardData.primaryField as Card["primaryField"],
      secondaryField: cardData.secondaryField as Card["secondaryField"],
      tertiaryField:
        (cardData.tertinaryField as Card["tertiaryField"]) ??
        (cardData as unknown as Card)["tertiaryField"],
      id: cardData.id.toString(),
      shopId: `${cardData.customer?.shopId}`,
      status: cardData.repairOrderStatus?.toString() ?? "unknown",
      statusEnum: `${cardData.repairOrderStatus}`,
      source: cardData.createdVia === "dashboard" ? "manual" : "tekmetric",
      backgroundColor: cardData.color ?? cardData.bgColor ?? "",
      notes: cardData.notes ?? [],
      customer:
        cardData.customer && customer.isV1(cardData.customer)
          ? customer.transformV1ToV2(cardData.customer)
          : {},
      vehicle:
        cardData.vehicle && vehicle.isV1(cardData.vehicle)
          ? vehicle.transformV1ToV2(cardData.vehicle)
          : {},
      technicianId: `${cardData.technicianId ? cardData.technicianId : ""}`,
      technicianIds: cardData.technicianIds
        ? cardData.technicianIds.map((techId) => `${techId}`)
        : [],
      serviceWriterId: `${cardData.serviceWriterId ? cardData.serviceWriterId : ""}`,
      label: "",
      sourceLastUpdated: new Date(),
      isStarred: cardData.isStarred ?? false,
      currentContainer: cardData.currentContainer,
      enteredContainerAt: new Date(),
      color: cardData.color ?? cardData.bgColor ?? "",
      timer: card.isV1(cardData)
        ? { timeLimitInMinutes: DEFAULT_TIMER_VALUE }
        : (cardData as Card).timer,
      history: cardData.timers.map((timer) => ({
        ...timer,
        enteredAt: timer.startTime,
        exitedAt: null,
        duration: null,
        id: shortUUID.generate(),
      })),
      keyTag: cardData.keytag ? `${cardData.keytag}` : null,
      cardNumber: cardData.repairOrderNumber
        ? `${cardData.repairOrderNumber}`
        : null,
      jobs: cardData.repairOrder?.jobs
        ? (cardData.repairOrder.jobs.map(
            (job: JobV1) =>
              ({
                technicianId: job.technicianId ? `${job.technicianId}` : null,
                loggedHours: job.loggedHours,
                laborHours: job.laborHours,
                laborTotal: job.laborTotal,
                completedDate: job.completedDate,
                name: job.name,
              }) as Job
          ) as Job[])
        : [],
    };
  },
  subscribe: (
    shopId: string,
    id: string,
    callback: (doc: Card | undefined) => void
  ) => {
    return firestore.subscribeDocument<Card>(
      `shops/${shopId}/cards/${id}`,
      callback,
      createConverter<CardV1, Card>(card)
    );
  },
  getContainerOptions: (cardData: Card, containers: Container[]) => {
    const containersByTechnician = containers.filter((cont) => {
      if (typeof cont.linkedTechnicianId === "number") {
        return `${cont.linkedTechnicianId}` === cardData.technicianId;
      }
      if (typeof cont.linkedTechnicianId === "string") {
        return `${cont.linkedTechnicianId}` === cardData.technicianId;
      }
      if (!cardData.technicianId) return false;
      return cont.linkedTechnicianId?.includes(cardData.technicianId);
    });

    const containersByLabel = containers.filter((cont) => {
      if (typeof cont.linkedSourceLabel === "string") {
        return `${cont.linkedSourceLabel}` === cardData.label;
      }
      if (!cardData.label) return false;
      return cont.linkedSourceLabel?.includes(cardData.label);
    });

    const commonContainerIds = extractCommonItems(
      containersByTechnician.map((cont) => cont.id),
      containersByLabel.map((cont) => cont.id)
    );

    return {
      syncTechOnly: containersByTechnician.map((c) => c.id),
      syncLabelOnly: containersByLabel.map((c) => c.id),
      syncBoth: commonContainerIds,
    };
  },
  startTimer: async (shopId: string, cardId: string, currentTimer: Timer) => {
    const update: Partial<Card> = {
      timer: {
        ...currentTimer,
        timeStarted: new Date(serverNow()),
        state: "running",
      },
    };

    await card.update(shopId, cardId, addUpdateData(update));

    return update;
  },
  pauseTimer: (
    shopId: string,
    cardId: string,
    accumulatedTime: number,
    currentTimer: Timer
  ) => {
    const update: Partial<Card> = {
      timer: {
        ...currentTimer,
        timeStarted: null,
        accumulatedTime: accumulatedTime,
        state: "paused",
      },
    };

    card.update(shopId, cardId, addUpdateData(update));
    return update;
  },
  stopTimer: (shopId: string, cardId: string, currentTimer: Timer) => {
    const update: Partial<Card> = {
      timer: {
        ...currentTimer,
        timeStarted: null,
        accumulatedTime: 0,
        state: "stopped",
      },
    };
    card.update(shopId, cardId, addUpdateData(update));
    return update;
  },
  changeTimerLimit: (
    shopId: string,
    cardId: string,
    timerLimit: string,
    currentTimer: Timer
  ) => {
    const update: Partial<Card> = {
      timer: { ...currentTimer, timeLimitInMinutes: timerLimit },
    };
    card.update(shopId, cardId, addUpdateData(update));
    return update;
  },
  changeCardColor: (shopId: string, cardId: string, color: string) => {
    const update: Partial<Card> = { color: color };
    card.update(shopId, cardId, addUpdateData(update));
    return update;
  },
  toggleStarred: (shopId: string, cardId: string, isStarred: boolean) => {
    const update: Partial<Card> = { isStarred };
    card.update(shopId, cardId, addUpdateData(update));
    return update;
  },

  calculateCompletedHours: (card: Card) => {
    const jobs = card.jobs ?? [];
    if (jobs.length === 0) {
      return card.totalLaborHours?.loggedLabor ?? 0;
    }
    return (
      Math.round(
        jobs.reduce((acc, job) => {
          if (job.authorized && job.laborHours) {
            return acc + (job.loggedHours ?? 0);
          }
          return acc;
        }, 0) * 10
      ) / 10
    );
  },

  calculateApprovedHours: (card: Card) => {
    const jobs = card.jobs ?? [];
    if (jobs.length === 0) {
      return card.totalLaborHours?.estimatedLabor ?? 0;
    }
    return (
      Math.round(
        jobs.reduce((acc, job) => {
          if (job.authorized && job.laborHours) {
            return acc + job.laborHours;
          }
          return acc;
        }, 0) * 10
      ) / 10
    );
  },

  paginate: async (
    shopId: string,
    pageSize: number = 20,
    lastDocumentSnapshot?: any,
    orderByField: string = "createdAt",
    orderDirection: "asc" | "desc" = "desc",
    searchOptions?: Array<{
      searchParam: string;
      opsString: any;
      equality: unknown;
    }>
  ) => {
    const path = `shops/${shopId}/cards`;

    if (searchOptions && searchOptions.length > 0) {
      return firestore.paginateDocumentsWithFilters<Card>(
        path,
        pageSize,
        lastDocumentSnapshot,
        createConverter<CardV1, Card>(card),
        orderByField,
        orderDirection,
        searchOptions
      );
    } else {
      return firestore.paginateDocuments<Card>(
        path,
        pageSize,
        lastDocumentSnapshot,
        createConverter<CardV1, Card>(card),
        orderByField,
        orderDirection
      );
    }
  },

  countTotal: async (
    shopId: string,
    searchOptions?: Array<{
      searchParam: string;
      opsString: any;
      equality: unknown;
    }>
  ) => {
    const path = `shops/${shopId}/cards`;

    if (searchOptions && searchOptions.length > 0) {
      return firestore.getFilteredDocumentCount(path, searchOptions);
    } else {
      return firestore.getDocumentCount(path);
    }
  },
};
